import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Heading } from '@/components/ui/shared/heading';
import { useUserState } from '@/components/context/UserContext';

import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import { Trans, useTranslation } from 'react-i18next';

import { useRegistration } from '../../CartPage/context/RegistrationContext';

import InputDropdown from '../../CartPage/Components/PatientRegistrationForm/InputDropdown';
import { usePatientRegistrationSchema } from '../../CartPage/Components/PatientRegistrationForm/usePatientRegistrationSchema';
import { useSettingsState } from '@/components/context/SettingsContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import z from 'zod';
import { useCartData } from '../../CartPage/context/CartDataContext';

const PAYMENT_TYPES = {
  EBank: 'EBank',
  Stebby: 'Stebby',
};

const isProduction = process.env.REACT_APP_IS_PRODUCTION === 'true';

function buildPayerPrefill(userState) {
  if (!userState?.isLoggedIn) return {};

  const u = userState.user ?? userState;

  return {
    payerFirstName: (u?.firstName || '').trim(),
    payerLastName: (u?.lastName || '').trim(),
    payerPersonalCode: (u?.personalCode || '').trim(),
    payerPhone: (u?.phoneNumber ?? u?.phone ?? '').trim(),
    payerEmail: (u?.email || '').trim(),
  };
}

function createApplyPrefill(form, lockedRef, forceRerender) {
  return function applyPrefill(data) {
    let changed = false;

    Object.entries(data).forEach(([name, value]) => {
      if (!value) return;

      const current = form.getValues(name);

      if (!current) {
        form.setValue(name, String(value).trim(), {
          shouldValidate: false,
          shouldDirty: false,
        });
        lockedRef.current.add(name);
        changed = true;
      }
    });

    if (changed) forceRerender((x) => x + 1);
  };
}

function createTrimOnBlur(form) {
  return function trimOnBlur(fieldApi) {
    return (e) => {
      fieldApi.onBlur?.();

      const trimmed = (e.target.value || '').trim();

      if (trimmed !== e.target.value) {
        form.setValue(fieldApi.name, trimmed, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
    };
  };
}

function toContactItems(contacts) {
  return (contacts ?? []).map((k) => {
    const name = (k?.name ?? '').trim();
    const surname = (k?.surname ?? '').trim();
    const personalCode = (k?.personalCode ?? '').trim();
    const espbiEsi = (k?.espbiEsi ?? '').trim();
    const display = `${name} ${surname}`.trim();

    return {
      value: display,
      label: display,
      person: { name, surname, personalCode, espbiEsi },
    };
  });
}

function shallowEqualKids(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];

    if (
      (x?.name ?? '') !== (y?.name ?? '') ||
      (x?.surname ?? '') !== (y?.surname ?? '') ||
      (x?.personalCode ?? '') !== (y?.personalCode ?? '')
    ) {
      return false;
    }
  }

  return true;
}

function deriveKids(contacts, maxAgeFromPersonalCode) {
  const out = [];

  (contacts ?? []).forEach((k) => {
    const pc = k?.personalCode;

    if (pc) {
      const isAdult = maxAgeFromPersonalCode(pc, 18);
      if (!isAdult) out.push(k);
    } else {
      out.push(k);
    }
  });

  return out;
}

function PaymentMethodSelector({ value, onChange, disabled = false }) {
  const options = useMemo(() => {
    const paymentOptions = [
      {
        value: PAYMENT_TYPES.EBank,
        title: 'Elektroninė bankininkystė',
        description: 'Apmokėjimas per banką.',
      },
    ];

    if (!isProduction) {
      paymentOptions.push({
        value: PAYMENT_TYPES.Stebby,
        title: 'Stebby',
        description: 'Apmokėjimas naudojant Stebby.',
      });
    }

    return paymentOptions;
  }, []);

  return (
    <section className="w-full space-y-3">
      <FormLabel>Mokėjimo būdas</FormLabel>

      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const selected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange?.(option.value)}
              className={[
                'rounded-lg border p-4 text-left transition',
                'focus:outline-none focus:ring-2 focus:ring-primary/40',
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-primary/60',
                disabled ? 'cursor-not-allowed opacity-60' : '',
              ].join(' ')}
            >
              <span className="flex items-start gap-3">
                <span
                  className={[
                    'mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                    selected ? 'border-primary' : 'border-gray-300',
                  ].join(' ')}
                >
                  {selected && (
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </span>

                <span>
                  <span className="block font-medium text-gray-900">
                    {option.title}
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">
                    {option.description}
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const ComplexBuyerInfo = React.forwardRef(
  (
    {
      onValid,
      onInvalid,
      paymentType = PAYMENT_TYPES.EBank,
      onPaymentTypeChange,
      isSubmitting = false,
    },
    ref,
  ) => {
    const userState = useUserState();
    const baseSchema = usePatientRegistrationSchema();

    const schema = useMemo(
      () =>
        baseSchema.and(
          z.object({
            agreePrivacy: z.literal(true, {
              errorMap: () => ({ message: 'Privalomas laukas' }),
            }),
            agreeComplexRules: z.literal(true, {
              errorMap: () => ({ message: 'Privalomas laukas' }),
            }),
          }),
        ),
      [baseSchema],
    );

    const { t } = useTranslation();
    const { agreements } = useSettingsState();
    const { userInfo, setUserInfo } = useRegistration();

    const [showUnderageDialog, setShowUnderageDialog] = useState(false);
    const [localChildSelected, setLocalChildSelected] = useState(false);
    const [localSelectedChildEspbiEsi, setLocalSelectedChildEspbiEsi] =
      useState('');

    const [validatePersonalCode, maxAgeFromPersonalCode] = useMemo(
      () => personalCodeValidation(),
      [],
    );

    const form = useForm({
      resolver: zodResolver(schema),
      defaultValues: {
        registerChild: false,
        payerFirstName: '',
        payerLastName: '',
        payerPhone: '',
        payerEmail: '',
        payerPersonalCode: '',
        patientFirstName: '',
        patientLastName: '',
        patientPersonalCode: '',
        agreePrivacy: false,
        agreeComplexRules: false,
      },
      mode: 'onSubmit',
      shouldFocusError: true,
    });

    useEffect(() => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(form);
        return;
      }

      ref.current = form;
    }, [form, ref]);

    const { watch, reset } = form;
    const registerChild = watch('registerChild');

    const lockedRef = useRef(new Set());
    const [, forceRerender] = useState(0);

    const applyPrefill = useMemo(
      () => createApplyPrefill(form, lockedRef, forceRerender),
      [form],
    );

    const trimOnBlur = useMemo(() => createTrimOnBlur(form), [form]);

    const [kids, setKids] = useState([]);

    useEffect(() => {
      const contacts = userState?.contacts ?? [];
      const nextKids = deriveKids(contacts, maxAgeFromPersonalCode);

      setKids((prev) => (shallowEqualKids(prev, nextKids) ? prev : nextKids));
    }, [userState?.contacts, maxAgeFromPersonalCode]);

    const peopleItems = useMemo(() => toContactItems(kids), [kids]);
    const hasSuggestions = !!userState?.isLoggedIn && peopleItems.length > 0;
    const { complex } = useCartData();

    useEffect(() => {
      if (!userInfo) return;

      reset({
        registerChild: Boolean(
          userInfo.registerChild ||
          userInfo.patientFirstName ||
          userInfo.patientLastName ||
          userInfo.patientPersonalCode,
        ),
        payerFirstName: userInfo.payerFirstName ?? '',
        payerLastName: userInfo.payerLastName ?? '',
        payerPhone: userInfo.payerPhone ?? '',
        payerEmail: userInfo.payerEmail ?? '',
        payerPersonalCode: userInfo.payerPersonalCode ?? '',
        patientFirstName: userInfo.patientFirstName ?? '',
        patientLastName: userInfo.patientLastName ?? '',
        patientPersonalCode: userInfo.patientPersonalCode ?? '',
        agreePrivacy: userInfo.agreePrivacy ?? false,
        agreeComplexRules: userInfo.agreeComplexRules ?? false,
      });

      setLocalChildSelected(Boolean(userInfo.childSelected));
      setLocalSelectedChildEspbiEsi(userInfo.selectedChildEspbiEsi || '');

      if (userInfo.paymentType) {
        onPaymentTypeChange?.(userInfo.paymentType);
      }
    }, [onPaymentTypeChange, reset, userInfo]);

    const didPrefillRef = useRef(false);

    useEffect(() => {
      if (!userState?.isLoggedIn) return;
      if (didPrefillRef.current) return;

      const prefill = buildPayerPrefill(userState);
      applyPrefill(prefill);
      didPrefillRef.current = true;
    }, [applyPrefill, userState]);

    const isLocked = (name) => lockedRef.current.has(name);

    const handleSubmit = form.handleSubmit(
      (values) => {
        let hasLocalError = false;

        if (!values.agreePrivacy) {
          form.setError('agreePrivacy', { message: 'Privalomas laukas' });
          hasLocalError = true;
        }

        if (!values.agreeComplexRules) {
          form.setError('agreeComplexRules', { message: 'Privalomas laukas' });
          hasLocalError = true;
        }

        if (hasLocalError) {
          const el = document.querySelector(
            '[data-error-anchor="agreePrivacy"], [data-error-anchor="agreeComplexRules"]',
          );
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        const merged = {
          ...values,
          paymentType,
          PaymentType: paymentType,
          childSelected: localChildSelected,
          selectedChildEspbiEsi: localSelectedChildEspbiEsi,
        };

        setUserInfo(merged);
        onValid?.(merged);
      },
      (errors) => {
        const firstErrorKey = Object.keys(errors)[0];

        if (firstErrorKey) {
          const el = document.querySelector(`[name="${firstErrorKey}"]`);

          if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }

        const pc = form.getValues('payerPersonalCode');
        const isUnder18 =
          pc && validatePersonalCode(pc) && !maxAgeFromPersonalCode(pc, 18);

        if (isUnder18) {
          setShowUnderageDialog(true);
          return;
        }

        onInvalid?.(form.formState.errors);
      },
    );

    const onPersonSelect = useMemo(
      () =>
        function onContactSelect(item) {
          const p = item?.person;
          if (!p) return;

          form.setValue('patientFirstName', p.name || '', {
            shouldDirty: true,
            shouldValidate: false,
          });
          form.setValue('patientLastName', p.surname || '', {
            shouldDirty: true,
            shouldValidate: false,
          });
          form.setValue('patientPersonalCode', p.personalCode || '', {
            shouldDirty: true,
            shouldValidate: false,
          });

          setLocalChildSelected(true);
          setLocalSelectedChildEspbiEsi(p.espbiEsi || '');
        },
      [form],
    );

    return (
      <div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Heading variant="6">Paciento informacija</Heading>

          {!complex?.simpleWorkflow && (
            <div className="flex items-center gap-2">
              <Switch
                id="registerChild"
                checked={registerChild}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    [
                      'patientFirstName',
                      'patientLastName',
                      'patientPersonalCode',
                    ].forEach((key) =>
                      form.setValue(key, '', { shouldDirty: true }),
                    );

                    setLocalChildSelected(false);
                    setLocalSelectedChildEspbiEsi('');
                  }

                  form.setValue('registerChild', checked);
                }}
              />
            </div>
          )}
        </div>

        <div className="space-y-6 mt-5">
          <Form {...form}>
            <form
              id="patient-form-id"
              className="space-y-6"
              onSubmit={handleSubmit}
            >
              {registerChild && (
                <div className="space-y-4">
                  <FormField
                    name="patientFirstName"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vardas</FormLabel>
                        <FormControl>
                          {hasSuggestions ? (
                            <InputDropdown
                              {...field}
                              placeholder="Pasirinkite arba įveskite vardą"
                              onBlur={trimOnBlur(field)}
                              items={peopleItems}
                              renderItem={(item) => (
                                <div className="flex flex-col">
                                  <span>
                                    {item.person.name} {item.person.surname}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {item.person.personalCode}
                                  </span>
                                </div>
                              )}
                              onItemSelect={onPersonSelect}
                            />
                          ) : (
                            <Input {...field} onBlur={trimOnBlur(field)} />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="patientLastName"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pavardė</FormLabel>
                        <FormControl>
                          <Input {...field} onBlur={trimOnBlur(field)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="patientPersonalCode"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Asmens kodas</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            onBlur={trimOnBlur(field)}
                            autoComplete="off"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {registerChild && (
                <Heading variant="6">Mokėtojo duomenys</Heading>
              )}

              <div className="space-y-4">
                <FormField
                  name="payerFirstName"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vardas</FormLabel>
                      <FormControl>
                        <Input
                          disabled={isLocked('payerFirstName')}
                          {...field}
                          onBlur={trimOnBlur(field)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="payerLastName"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pavardė</FormLabel>
                      <FormControl>
                        <Input
                          disabled={isLocked('payerLastName')}
                          {...field}
                          onBlur={trimOnBlur(field)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="payerPhone"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefono numeris</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          disabled={isLocked('payerPhone')}
                          {...field}
                          onBlur={trimOnBlur(field)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="payerEmail"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>El. pašto adresas</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          disabled={isLocked('payerEmail')}
                          {...field}
                          onBlur={trimOnBlur(field)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  name="payerPersonalCode"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asmens kodas</FormLabel>
                      <FormControl>
                        <Input
                          disabled={isLocked('payerPersonalCode')}
                          {...field}
                          onBlur={trimOnBlur(field)}
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {!complex?.simpleWorkflow && (
                <PaymentMethodSelector
                  value={paymentType}
                  onChange={onPaymentTypeChange}
                  disabled={isSubmitting}
                />
              )}

              <section className="w-full space-y-3">
                <FormLabel>{t('cart.agreementGroulLabel')}</FormLabel>

                <FormField
                  control={form.control}
                  name="agreePrivacy"
                  render={({ field }) => (
                    <FormItem
                      className="flex items-start gap-2 [&_[data-slot=label]]:leading-5"
                      data-error-anchor={field.name}
                    >
                      <FormControl>
                        <Checkbox
                          id="agree-privacy"
                          className="mt-[2px]"
                          checked={!!field.value}
                          onCheckedChange={(v) => field.onChange(Boolean(v))}
                        />
                      </FormControl>

                      <div className="space-y-1">
                        <Label htmlFor="agree-privacy" layout="text">
                          <Trans
                            i18nKey="cart.fieldAgreement2Label"
                            components={{
                              a: (
                                <a
                                  href={agreements.personalData.url}
                                  className="underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              ),
                            }}
                          />
                        </Label>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="agreeComplexRules"
                  render={({ field }) => (
                    <FormItem
                      className="flex items-start gap-2 [&_[data-slot=label]]:leading-5"
                      data-error-anchor={field.name}
                    >
                      <FormControl>
                        <Checkbox
                          id="agree-complex-rules"
                          className="mt-[2px]"
                          checked={!!field.value}
                          onCheckedChange={(v) => field.onChange(Boolean(v))}
                        />
                      </FormControl>

                      <div className="flex-1">
                        <Label htmlFor="agree-complex-rules" layout="text">
                          <Trans
                            i18nKey="coupons.fieldAgreementComplexLabel"
                            components={{
                              a: (
                                <a
                                  href={agreements.complexServiceRules.url}
                                  className="underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                />
                              ),
                            }}
                          />
                        </Label>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </section>

              {showUnderageDialog && (
                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                  Mokėtojas negali būti nepilnametis. Pasirinkite registraciją
                  vaikui ir įveskite mokėtojo duomenis.
                </div>
              )}
            </form>
          </Form>
        </div>
      </div>
    );
  },
);

ComplexBuyerInfo.displayName = 'ComplexBuyerInfo';

export default ComplexBuyerInfo;
